import { BoxGeometry, ConeGeometry, Group, Mesh, MeshStandardMaterial, PlaneGeometry, SphereGeometry, type Object3D } from 'three';
import { TRAFFIC_KINDS } from '../content/traffic';
import { trafficMesh } from '../entities/vehicles/catalog';
import { flatMaterial } from '../render/materials/cache';
import { canvasTexture } from '../render/materials/canvasTexture';
import { LAYOUT } from '../world/road/roadLayout';
import type { BusState, Checkpoint } from './RunState';

const OFFICER_X = LAYOUT.kerbX + 0.7;

function officer(): { group: Group; arm: Group } {
  const group = new Group();
  const part = (w: number, h: number, d: number, color: string, x: number, y: number, z = 0): Mesh => {
    const mesh = new Mesh(new BoxGeometry(w, h, d), flatMaterial(color));
    mesh.position.set(x, y, z);
    mesh.castShadow = true;
    return mesh;
  };
  const head = new Mesh(new SphereGeometry(0.17, 10, 8), flatMaterial('#6b4423'));
  head.position.set(0, 1.78, 0);
  const arm = new Group();
  arm.position.set(-0.3, 1.5, 0);
  const sleeve = part(0.14, 0.7, 0.14, '#5b8fd1', 0, 0.3);
  const hand = part(0.16, 0.2, 0.1, '#6b4423', 0, 0.72);
  arm.add(sleeve, hand);
  group.add(
    part(0.2, 0.85, 0.2, '#1b2a41', -0.12, 0.43), part(0.2, 0.85, 0.2, '#1b2a41', 0.12, 0.43),
    part(0.5, 0.65, 0.3, '#5b8fd1', 0, 1.2), part(0.52, 0.18, 0.32, '#c8f31d', 0, 1.18),
    part(0.14, 0.62, 0.14, '#5b8fd1', 0.33, 1.22), head, part(0.36, 0.1, 0.34, '#f4f4f4', 0, 1.97), part(0.3, 0.04, 0.16, '#111111', 0, 1.93, 0.2), arm,
  );
  return { group, arm };
}

function cone(x: number, z: number): Mesh {
  const mesh = new Mesh(new ConeGeometry(0.22, 0.65, 8), flatMaterial('#ff6a13'));
  mesh.position.set(x, 0.33, z);
  mesh.castShadow = true;
  return mesh;
}

function policeSign(): Mesh {
  const texture = canvasTexture(256, 128, (ctx) => {
    ctx.fillStyle = '#12306b';
    ctx.fillRect(0, 0, 256, 128);
    ctx.fillStyle = '#ffffff';
    ctx.font = 'bold 54px Impact, sans-serif';
    ctx.textAlign = 'center';
    ctx.fillText('POLICE', 128, 62);
    ctx.fillStyle = '#fcd116';
    ctx.font = 'bold 36px Arial, sans-serif';
    ctx.fillText('CHECK POINT', 128, 106);
  });
  const mesh = new Mesh(new PlaneGeometry(1.6, 0.8), new MeshStandardMaterial({ map: texture, emissive: '#ffffff', emissiveMap: texture, emissiveIntensity: 0.25 }));
  mesh.position.set(OFFICER_X + 0.4, 1.3, 7);
  return mesh;
}

/** A police checkpoint: an officer by the kerb (who waves, then walks to your window), cones, a sign and a parked police car with flashing lights. */
export class CheckpointView {
  readonly group = new Group();
  private readonly person = officer();
  private readonly red = new MeshStandardMaterial({ color: '#ff1d1d', emissive: '#ff1d1d', emissiveIntensity: 1 });
  private readonly blue = new MeshStandardMaterial({ color: '#1d4dff', emissive: '#1d4dff', emissiveIntensity: 1 });
  private clock = 0;
  private id = -1;

  constructor() {
    const saloon = TRAFFIC_KINDS.find((k) => k.id === 'saloon');
    const car: Object3D | null = saloon ? trafficMesh(saloon, 2) : null;
    if (car) {
      car.position.set(LAYOUT.sidewalkX + 0.2, 0.2, -7);
      car.rotation.y = 0.12;
      this.group.add(car);
    }
    const bar = new Group();
    const lamp = (material: MeshStandardMaterial, x: number): Mesh => {
      const mesh = new Mesh(new BoxGeometry(0.36, 0.14, 0.2), material);
      mesh.position.set(x, 0, 0);
      return mesh;
    };
    bar.add(lamp(this.red, -0.25), lamp(this.blue, 0.25));
    bar.position.set(LAYOUT.sidewalkX + 0.2, 1.8, -7.2);
    this.group.add(bar);
    for (let i = 0; i < 6; i += 1) this.group.add(cone(LAYOUT.kerbX - 0.35 - (i % 2) * 0.2, 2 + i * 2.6));
    this.group.add(this.person.group, policeSign());
    this.group.visible = false;
  }

  update(cp: Checkpoint | null, bus: BusState, dt: number): void {
    this.group.visible = cp !== null;
    if (!cp) {
      this.id = -1;
      return;
    }
    this.clock += dt;
    const { person } = this;
    // The officer faces the oncoming bus.
    person.group.rotation.y = 0;
    const flash = Math.floor(this.clock * 4) % 2 === 0;
    this.red.emissiveIntensity = flash ? 1.4 : 0.1;
    this.blue.emissiveIntensity = flash ? 0.1 : 1.4;

    if (this.id !== cp.id) {
      this.id = cp.id;
      this.group.position.set(0, 0, -cp.s);
      person.group.position.set(OFFICER_X, 0.2, 0);
    }
    // While checking, he walks over to the driver's window; afterwards he steps back to the kerb.
    const checking = cp.phase === 'checking';
    const targetX = checking ? bus.x + bus.size.width / 2 + 0.6 : OFFICER_X;
    const targetZ = checking ? -(bus.s - cp.s) + bus.size.length * 0.15 : 0;
    person.group.position.x += (targetX - person.group.position.x) * Math.min(1, dt * 3);
    person.group.position.z += (targetZ - person.group.position.z) * Math.min(1, dt * 3);
    // Arm out over the road, waving; a steady raised palm while checking; down when done.
    const waving = cp.phase === 'approach' || cp.phase === 'ran';
    person.arm.rotation.z = waving ? -2.1 + Math.sin(this.clock * 7) * 0.35 : checking ? -1.6 : -0.15;
  }
}
